import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBook } from '../context/BookContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { userApi, serviceApi, reviewApi, bookingApi, profileApi, calendarApi, favoriteApi, reportApi } from '../lib/api';
import ProfileCompletionModal from '../components/ProfileCompletionModal';
import { FireRating } from '../components/TalentCard';
import SEO from '../components/SEO';
import {
  Flame, MapPin, BadgeCheck, Calendar as CalendarIcon, Clock, 
  DollarSign, Play, Image, ExternalLink, Loader2, MessageSquare,
  Shield, ShieldCheck, AlertCircle, CalendarDays, Briefcase, BookOpen, Plus,
  Heart, Flag, Ban, MoreVertical
} from 'lucide-react';
import { format, isSameDay, parseISO } from 'date-fns';

const TalentProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { addToBook, isInBook } = useBook();
  
  const [talent, setTalent] = useState(null);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [talentCalendar, setTalentCalendar] = useState([]);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  
  // Profile completion
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [profileComplete, setProfileComplete] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'message' or 'book'
  
  // Booking state
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [bookingNotes, setBookingNotes] = useState('');
  const [addInsurance, setAddInsurance] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  
  // Favorites & Reporting
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    loadTalentData();
    if (isAuthenticated) {
      checkProfileCompletion();
      checkFavoriteStatus();
    }
  }, [id, isAuthenticated]);

  const checkFavoriteStatus = async () => {
    try {
      const response = await favoriteApi.check(id);
      setIsFavorited(response.data.is_favorited);
    } catch (error) {
      console.error('Failed to check favorite status:', error);
    }
  };

  const handleToggleFavorite = async () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=/talent/${id}`);
      return;
    }
    
    setFavoriteLoading(true);
    try {
      if (isFavorited) {
        await favoriteApi.remove(id);
        setIsFavorited(false);
        toast.success('Removed from favorites');
      } else {
        await favoriteApi.add(id);
        setIsFavorited(true);
        toast.success('Added to favorites!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update favorites');
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handleReport = async () => {
    if (!reportReason) {
      toast.error('Please select a reason for reporting');
      return;
    }
    
    setReportLoading(true);
    try {
      await reportApi.report(id, reportReason, reportDescription);
      toast.success('Report submitted. Our team will review it shortly.');
      setReportDialogOpen(false);
      setReportReason('');
      setReportDescription('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setReportLoading(false);
    }
  };

  const handleBlock = async () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=/talent/${id}`);
      return;
    }
    
    try {
      await reportApi.block(id);
      toast.success('User blocked. You will no longer see their profile.');
      navigate('/browse');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block user');
    }
  };

  const checkProfileCompletion = async () => {
    try {
      const response = await profileApi.getCompletionStatus();
      setProfileComplete(response.data.profile_completed);
    } catch (error) {
      console.error('Failed to check profile completion:', error);
    }
  };

  const handleProtectedAction = (action) => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=/talent/${id}`);
      return;
    }
    
    if (!profileComplete) {
      setPendingAction(action);
      setShowCompletionModal(true);
      return;
    }
    
    if (action === 'message') {
      navigate(`/messages?to=${id}`);
    } else if (action === 'book') {
      setBookingOpen(true);
    }
  };

  const handleCompletionDone = () => {
    setProfileComplete(true);
    setShowCompletionModal(false);
    
    // Execute the pending action
    if (pendingAction === 'message') {
      navigate(`/messages?to=${id}`);
    } else if (pendingAction === 'book') {
      setBookingOpen(true);
    }
    setPendingAction(null);
  };

  const loadTalentData = async () => {
    setLoading(true);
    try {
      const [talentRes, servicesRes, reviewsRes, calendarRes] = await Promise.all([
        userApi.getTalent(id),
        serviceApi.getByTalent(id),
        reviewApi.getByTalent(id),
        calendarApi.getTalentPublic(id).catch(() => ({ data: [] }))
      ]);
      setTalent(talentRes.data);
      setServices(servicesRes.data);
      setReviews(reviewsRes.data);
      setTalentCalendar(calendarRes.data || []);
      
      // Track recently viewed
      const talentData = talentRes.data;
      if (talentData && talentData.user_id) {
        const viewedItem = {
          id: talentData.user_id,
          screenName: talentData.screen_name,
          name: talentData.screen_name || talentData.name,
          image: talentData.picture || (talentData.portfolio_images && talentData.portfolio_images[0]),
          category: talentData.main_category || talentData.categories?.[0] || 'Talent',
          type: 'talent',
          viewedAt: new Date().toISOString()
        };
        
        try {
          const stored = localStorage.getItem('bookd_recently_viewed');
          let recentList = stored ? JSON.parse(stored) : [];
          // Remove if already exists
          recentList = recentList.filter(item => item.id !== talentData.user_id);
          // Add to front
          recentList.unshift(viewedItem);
          // Keep only last 12
          recentList = recentList.slice(0, 12);
          localStorage.setItem('bookd_recently_viewed', JSON.stringify(recentList));
        } catch (e) {
          console.error('Failed to save recently viewed:', e);
        }
      }
    } catch (error) {
      toast.error('Failed to load talent profile');
      navigate('/browse');
    } finally {
      setLoading(false);
    }
  };

  const handleBook = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to book');
      navigate('/login');
      return;
    }

    if (!selectedService || !selectedDate || !selectedTime) {
      toast.error('Please select a service, date, and time');
      return;
    }

    setBookingLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const startTime = `${dateStr}T${selectedTime}:00`;

      await bookingApi.create({
        service_id: selectedService.service_id,
        talent_id: id,
        start_time: startTime,
        notes: bookingNotes,
        add_insurance: addInsurance
      });

      toast.success('Booking created! Proceed to payment.');
      setBookingOpen(false);
      navigate('/bookings');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setBookingLoading(false);
    }
  };

  const timeSlots = [
    '09:00', '10:00', '11:00', '12:00', '13:00', '14:00',
    '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!talent) return null;

  const coverImage = talent.portfolio_images?.[0] || 'https://images.pexels.com/photos/1763069/pexels-photo-1763069.jpeg';

  return (
    <div className="min-h-screen pb-12">
      {/* SEO Meta Tags */}
      <SEO 
        profile={talent}
        url={`/talent/${talent.screen_name || talent.user_id}`}
      />
      
      {/* Cover Image */}
      <div className="h-64 md:h-80 relative">
        <img src={coverImage} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 -mt-24 relative z-10">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Profile Header */}
            <Card className="glass-panel border-0">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  <Avatar className="h-32 w-32 border-4 border-background">
                    <AvatarImage src={talent.picture} />
                    <AvatarFallback className="text-4xl bg-primary text-primary-foreground">
                      {talent.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                        {talent.name}
                      </h1>
                      {talent.is_verified && (
                        <Badge className="verified-badge">
                          <BadgeCheck className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                      {talent.is_insured && (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          <ShieldCheck className="h-3 w-3 mr-1" />
                          Insured
                        </Badge>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2 mb-3">
                      {talent.categories?.map((cat, idx) => (
                        <Badge key={idx} variant="secondary" className="category-badge">
                          {cat}
                        </Badge>
                      ))}
                    </div>

                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      {talent.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {talent.location}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Flame className="h-4 w-4 text-orange-500 fill-orange-500" />
                        {talent.rating?.toFixed(1) || '0.0'} ({talent.review_count || 0} reviews)
                      </span>
                      <span className="flex items-center gap-1">
                        <Briefcase className="h-4 w-4" />
                        {talent.completed_bookings || 0} jobs completed
                      </span>
                    </div>

                    {talent.bio && (
                      <p className="mt-4 text-muted-foreground">{talent.bio}</p>
                    )}

                    {/* Action Buttons */}
                    {user?.user_id !== talent.user_id && (
                      <div className="mt-4 flex items-center gap-2 flex-wrap">
                        {/* Message Button */}
                        <Button
                          variant="outline"
                          className="rounded-full"
                          onClick={() => handleProtectedAction('message')}
                          data-testid="message-talent-btn"
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Message
                        </Button>

                        {/* Favorite Button */}
                        <Button
                          variant="outline"
                          className={`rounded-full ${isFavorited ? 'text-red-500 border-red-500/50 hover:bg-red-500/10' : ''}`}
                          onClick={handleToggleFavorite}
                          disabled={favoriteLoading}
                          data-testid="favorite-talent-btn"
                        >
                          <Heart className={`h-4 w-4 mr-2 ${isFavorited ? 'fill-current' : ''}`} />
                          {isFavorited ? 'Saved' : 'Save'}
                        </Button>

                        {/* Report/Block Dropdown */}
                        <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="rounded-full text-muted-foreground hover:text-red-500"
                              data-testid="report-menu-btn"
                            >
                              <Flag className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-md">
                            <DialogHeader>
                              <DialogTitle className="flex items-center gap-2">
                                <Flag className="h-5 w-5 text-red-500" />
                                Report User
                              </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div className="space-y-2">
                                <Label>Reason for reporting *</Label>
                                <Select value={reportReason} onValueChange={setReportReason}>
                                  <SelectTrigger className="rounded-lg">
                                    <SelectValue placeholder="Select a reason" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="harassment">Harassment or bullying</SelectItem>
                                    <SelectItem value="spam">Spam or misleading</SelectItem>
                                    <SelectItem value="inappropriate">Inappropriate content</SelectItem>
                                    <SelectItem value="fraud">Fraud or scam</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              <div className="space-y-2">
                                <Label>Additional details (optional)</Label>
                                <Textarea
                                  value={reportDescription}
                                  onChange={(e) => setReportDescription(e.target.value.slice(0, 1000))}
                                  placeholder="Please provide any additional context..."
                                  className="rounded-lg min-h-[100px]"
                                />
                                <p className="text-xs text-muted-foreground text-right">
                                  {reportDescription.length}/1000
                                </p>
                              </div>
                              
                              <Separator />
                              
                              <div className="flex gap-3">
                                <Button
                                  variant="outline"
                                  className="flex-1 rounded-lg"
                                  onClick={() => setReportDialogOpen(false)}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  onClick={handleReport}
                                  disabled={reportLoading || !reportReason}
                                  className="flex-1 rounded-lg bg-red-500 hover:bg-red-600"
                                  data-testid="submit-report-btn"
                                >
                                  {reportLoading ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                  ) : (
                                    <Flag className="h-4 w-4 mr-2" />
                                  )}
                                  Submit Report
                                </Button>
                              </div>
                              
                              <Separator />
                              
                              <Button
                                variant="ghost"
                                className="w-full rounded-lg text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to block this user? You will no longer see their profile or receive messages from them.')) {
                                    handleBlock();
                                  }
                                }}
                                data-testid="block-user-btn"
                              >
                                <Ban className="h-4 w-4 mr-2" />
                                Block this user
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs defaultValue="services" className="space-y-6">
              <TabsList className="bg-muted/50 rounded-xl p-1">
                <TabsTrigger value="services" className="rounded-lg">Services</TabsTrigger>
                <TabsTrigger value="portfolio" className="rounded-lg">Portfolio</TabsTrigger>
                <TabsTrigger value="reviews" className="rounded-lg">Reviews</TabsTrigger>
                <TabsTrigger value="calendar" className="rounded-lg">Calendar</TabsTrigger>
              </TabsList>

              <TabsContent value="services" className="space-y-4">
                {services.length > 0 ? (
                  services.map(service => (
                    <Card key={service.service_id} className="hover:border-primary/50 transition-colors">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>
                              {service.title}
                            </h3>
                            <p className="text-muted-foreground text-sm mb-3">
                              {service.description}
                            </p>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="flex items-center gap-1 text-muted-foreground">
                                <Clock className="h-4 w-4" />
                                {service.duration_minutes} min
                              </span>
                              <span className="flex items-center gap-1 price-tag text-lg">
                                <DollarSign className="h-4 w-4" />
                                {service.price}
                              </span>
                            </div>
                          </div>
                          <Button
                            onClick={() => { setSelectedService(service); handleProtectedAction('book'); }}
                            className="rounded-full"
                            data-testid={`book-service-${service.service_id}`}
                          >
                            Book Now
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <p className="text-center text-muted-foreground py-8">No services listed yet</p>
                )}
              </TabsContent>

              <TabsContent value="portfolio" className="space-y-6">
                {/* Images */}
                {talent.portfolio_images?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                      <Image className="h-5 w-5" />
                      Photos
                    </h3>
                    <div className="portfolio-grid">
                      {talent.portfolio_images.map((img, idx) => (
                        <div key={idx} className="aspect-square rounded-xl overflow-hidden">
                          <img src={img} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Videos */}
                {talent.portfolio_videos?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                      <Play className="h-5 w-5" />
                      Videos
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {talent.portfolio_videos.map((video, idx) => (
                        <a
                          key={idx}
                          href={video}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                        >
                          <Play className="h-5 w-5 text-primary" />
                          <span className="truncate">{video}</span>
                          <ExternalLink className="h-4 w-4 ml-auto" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {(!talent.portfolio_images?.length && !talent.portfolio_videos?.length) && (
                  <p className="text-center text-muted-foreground py-8">No portfolio items yet</p>
                )}
              </TabsContent>

              <TabsContent value="reviews" className="space-y-4">
                {/* Reviews Summary */}
                {reviews?.reviews?.length > 0 && (
                  <div className="flex items-center gap-4 mb-4 p-4 rounded-xl bg-muted/50">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-primary">{talent?.rating?.toFixed(1) || '0.0'}</p>
                      <div className="flex justify-center">
                        <FireRating rating={talent?.rating || 0} showValue={false} />
                      </div>
                    </div>
                    <Separator orientation="vertical" className="h-12" />
                    <div>
                      <p className="text-sm text-muted-foreground">{reviews?.total || 0} reviews</p>
                    </div>
                  </div>
                )}

                {reviews?.reviews?.length > 0 ? (
                  reviews.reviews.map(review => (
                    <Card key={review.review_id}>
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <Avatar>
                            <AvatarImage src={review.reviewer_picture} />
                            <AvatarFallback>{review.reviewer_name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{review.reviewer_name}</span>
                              <div className="flex">
                                {[...Array(5)].map((_, i) => (
                                  <Flame
                                    key={i}
                                    className={`h-4 w-4 ${i < review.rating ? 'text-orange-500 fill-orange-500' : 'text-muted-foreground/30'}`}
                                  />
                                ))}
                              </div>
                            </div>
                            {review.text && (
                              <p className="text-muted-foreground mt-2">{review.text}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-2">
                              {new Date(review.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <p className="text-center text-muted-foreground py-8">No reviews yet</p>
                )}
              </TabsContent>

              {/* Calendar Tab */}
              <TabsContent value="calendar" className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Calendar Widget */}
                  <Card>
                    <CardContent className="p-4">
                      <Calendar
                        mode="single"
                        selected={calendarDate}
                        onSelect={setCalendarDate}
                        className="rounded-md"
                        modifiers={{
                          booked: talentCalendar
                            .filter(e => e.type === 'booking' || e.type === 'busy')
                            .map(e => parseISO(e.date))
                        }}
                        modifiersStyles={{
                          booked: { backgroundColor: 'rgba(239, 68, 68, 0.2)', color: 'rgb(239, 68, 68)' }
                        }}
                      />
                    </CardContent>
                  </Card>

                  {/* Events for Selected Date */}
                  <div className="space-y-3">
                    <h4 className="font-semibold flex items-center gap-2">
                      <CalendarDays className="h-4 w-4 text-primary" />
                      {calendarDate ? format(calendarDate, 'MMMM d, yyyy') : 'Select a date'}
                    </h4>
                    
                    {calendarDate && (
                      <>
                        {talentCalendar.filter(e => 
                          isSameDay(parseISO(e.date), calendarDate)
                        ).length > 0 ? (
                          talentCalendar
                            .filter(e => isSameDay(parseISO(e.date), calendarDate))
                            .map((event, idx) => (
                              <Card key={idx} className={`${event.type === 'available' ? 'border-green-500/30' : 'border-red-500/30'}`}>
                                <CardContent className="p-4">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-3 h-3 rounded-full ${event.type === 'available' ? 'bg-green-500' : 'bg-red-500'}`} />
                                    <div>
                                      <p className="font-medium">{event.title || (event.type === 'available' ? 'Available' : 'Busy')}</p>
                                      {event.start_time && event.end_time && (
                                        <p className="text-sm text-muted-foreground">
                                          {event.start_time} - {event.end_time}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            ))
                        ) : (
                          <Card className="border-green-500/30">
                            <CardContent className="p-4 text-center">
                              <p className="text-green-500 font-medium">Available</p>
                              <p className="text-sm text-muted-foreground">No events scheduled</p>
                              <Button 
                                onClick={() => handleProtectedAction('book')} 
                                className="mt-3 rounded-full"
                                size="sm"
                              >
                                Book This Day
                              </Button>
                            </CardContent>
                          </Card>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Quick Actions */}
          <div className="space-y-6">
            <Card className="glass-panel border-0 sticky top-24">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Quick Book</span>
                  {talent.hourly_rate && (
                    <span className="price-tag text-xl">${talent.hourly_rate}/hr</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {services.length > 0 ? (
                  <>
                    <Select
                      value={selectedService?.service_id || ''}
                      onValueChange={(val) => setSelectedService(services.find(s => s.service_id === val))}
                    >
                      <SelectTrigger data-testid="quick-book-service-select">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map(service => (
                          <SelectItem key={service.service_id} value={service.service_id}>
                            {service.title} - ${service.price}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Button
                      className="w-full rounded-xl btn-glow"
                      onClick={() => setBookingOpen(true)}
                      disabled={!selectedService}
                      data-testid="quick-book-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      Book Now
                    </Button>
                    
                    {/* Add to Book Button - For Bookers */}
                    {user?.user_type === 'client' && (
                      <Button
                        variant="outline"
                        className={`w-full rounded-xl mt-2 ${isInBook(talent.user_id) ? 'border-amber-500 text-amber-500' : ''}`}
                        onClick={() => {
                          if (isInBook(talent.user_id)) {
                            navigate('/my-book');
                          } else {
                            addToBook(talent);
                          }
                        }}
                        data-testid="add-to-book-btn"
                      >
                        <BookOpen className="mr-2 h-4 w-4" />
                        {isInBook(talent.user_id) ? 'View in Book' : 'Add to Book'}
                      </Button>
                    )}
                  </>
                ) : (
                  <p className="text-center text-muted-foreground">No services available</p>
                )}

                {/* Social Links */}
                {talent.social_links && Object.keys(talent.social_links).length > 0 && (
                  <div className="pt-4 border-t border-border">
                    <p className="text-sm font-medium mb-3">Connect</p>
                    <div className="flex gap-2">
                      {Object.entries(talent.social_links).map(([platform, url]) => (
                        <a
                          key={platform}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Syne, sans-serif' }}>
              Book {selectedService?.title}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {selectedService && (
              <div className="p-4 rounded-xl bg-muted/50">
                <p className="font-medium">{selectedService.title}</p>
                <p className="text-sm text-muted-foreground">
                  {selectedService.duration_minutes} min • ${selectedService.price}
                </p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium mb-2 block">Select Date</label>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => date < new Date()}
                className="rounded-xl border"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Select Time</label>
              <Select value={selectedTime} onValueChange={setSelectedTime}>
                <SelectTrigger data-testid="booking-time-select">
                  <SelectValue placeholder="Choose a time" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map(time => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Notes (Optional)</label>
              <Textarea
                placeholder="Any special requirements..."
                value={bookingNotes}
                onChange={(e) => setBookingNotes(e.target.value)}
                className="rounded-xl"
                data-testid="booking-notes"
              />
            </div>

            {/* Insurance Option */}
            <div className={`p-4 rounded-xl border transition-colors ${addInsurance ? 'bg-green-500/10 border-green-500/30' : 'bg-muted/50 border-border'}`}>
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${addInsurance ? 'bg-green-500/20' : 'bg-muted'}`}>
                  <Shield className={`h-5 w-5 ${addInsurance ? 'text-green-400' : 'text-muted-foreground'}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">Booking Protection</p>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={addInsurance}
                        onChange={(e) => setAddInsurance(e.target.checked)}
                        className="sr-only peer"
                        data-testid="insurance-checkbox"
                      />
                      <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:bg-green-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                    </label>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Add 5% insurance fee for cancellation protection and dispute resolution
                  </p>
                  {selectedService && (
                    <p className="text-sm text-green-400 mt-2">
                      +${(selectedService.price * 0.05).toFixed(2)} insurance fee
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Price Summary */}
            {selectedService && (
              <div className="p-4 rounded-xl bg-muted/50 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Service</span>
                  <span>${selectedService.price.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Platform Fee (10%)</span>
                  <span>${(selectedService.price * 0.10).toFixed(2)}</span>
                </div>
                {addInsurance && (
                  <div className="flex justify-between text-sm text-green-400">
                    <span>Insurance (5%)</span>
                    <span>${(selectedService.price * 0.05).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold pt-2 border-t border-border">
                  <span>Total</span>
                  <span className="text-primary">
                    ${(selectedService.price * (1.10 + (addInsurance ? 0.05 : 0))).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            <Button
              className="w-full rounded-xl btn-glow"
              onClick={handleBook}
              disabled={bookingLoading || !selectedDate || !selectedTime}
              data-testid="confirm-booking-btn"
            >
              {bookingLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Booking...
                </>
              ) : (
                <>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  Confirm Booking
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile Completion Modal */}
      <ProfileCompletionModal
        isOpen={showCompletionModal}
        onClose={() => {
          setShowCompletionModal(false);
          setPendingAction(null);
        }}
        onComplete={handleCompletionDone}
      />
    </div>
  );
};

export default TalentProfile;
